import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, Search, Filter, BookOpen, Scale, CheckCircle, XCircle, Clock, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  annual_return: 'Annual Return',
  financial_statement: 'Financial Statements',
  auditor: 'Auditor',
  strike_off: 'Strike-off',
  dormancy: 'Dormancy',
  director: 'Director',
  compliance: 'Compliance',
};

const CATEGORY_COLORS = {
  annual_return: 'bg-blue-50 text-blue-700 border-blue-200',
  financial_statement: 'bg-violet-50 text-violet-700 border-violet-200',
  auditor: 'bg-amber-50 text-amber-700 border-amber-200',
  strike_off: 'bg-red-50 text-red-700 border-red-200',
  dormancy: 'bg-slate-50 text-slate-700 border-slate-200',
  director: 'bg-teal-50 text-teal-700 border-teal-200',
  compliance: 'bg-cyan-50 text-cyan-700 border-cyan-200',
};

export default function AdminFormLibrary() {
  const [forms, setForms] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterActive, setFilterActive] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [formsRes, thresholdsRes] = await Promise.all([
        fetch(`${API}/api/admin/form-library`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/regulatory-thresholds`, { headers }).then(r => r.json()),
      ]);
      setForms(formsRes);
      setThresholds(thresholdsRes);
    } catch (e) {
      toast.error('Failed to load form library');
    } finally {
      setLoading(false);
    }
  };

  const categories = [...new Set(forms.map(f => f.category))];

  const filtered = forms.filter(f => {
    if (search && !f.form_code.toLowerCase().includes(search.toLowerCase()) && !f.form_name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory !== 'all' && f.category !== filterCategory) return false;
    if (filterActive === 'active' && !f.is_active) return false;
    if (filterActive === 'legacy' && f.is_active !== false) return false;
    return true;
  });

  const activeCount = forms.filter(f => f.is_active !== false).length;
  const legacyCount = forms.filter(f => f.is_active === false).length;
  const ccfsCount = forms.filter(f => f.ccfs_covered).length;

  if (loading) return <div className="flex items-center justify-center py-20 text-sm text-slate-400">Loading form library...</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6" data-testid="admin-form-library">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#1e3a5f]" /> MCA Form Library
        </h1>
        <p className="text-sm text-slate-500 mt-1">Master reference of all MCA forms with applicability rules, CCFS coverage, and filing requirements.</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Total Forms</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{forms.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Active (Current Act)</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{activeCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Legacy (1956 Act)</p>
          <p className="text-2xl font-bold text-slate-400 mt-1">{legacyCount}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">CCFS Covered</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">{ccfsCount}</p>
        </div>
      </div>

      {/* Regulatory Thresholds */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-[#1e3a5f] mb-3 flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5" /> Small Company Thresholds (Section 2(85))
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {thresholds.map((t, i) => (
              <div key={i} className={`rounded-lg border p-3 ${!t.effective_to ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50/50 opacity-70'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {!t.effective_to && <span className="text-[9px] font-bold text-emerald-600 uppercase bg-emerald-100 px-2 py-0.5 rounded">Current</span>}
                  {t.effective_to && <span className="text-[9px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded">Superseded</span>}
                </div>
                <p className="text-xs font-semibold text-slate-800">{t.label}</p>
                <p className="text-[10px] text-slate-500 mt-1">
                  Effective: {t.effective_from} {t.effective_to ? `to ${t.effective_to}` : 'onwards'}
                </p>
                {t.source && <p className="text-[10px] text-slate-400 mt-1">{t.source}</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filter Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input
            placeholder="Search forms..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-8 text-xs"
            data-testid="form-library-search"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <button onClick={() => setFilterCategory('all')} className={`text-[10px] px-2 py-1 rounded ${filterCategory === 'all' ? 'bg-[#1e3a5f] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilterCategory(cat)} className={`text-[10px] px-2 py-1 rounded ${filterCategory === cat ? 'bg-[#1e3a5f] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              {CATEGORY_LABELS[cat] || cat}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setFilterActive('all')} className={`text-[10px] px-2 py-1 rounded ${filterActive === 'all' ? 'bg-[#1e3a5f] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All</button>
          <button onClick={() => setFilterActive('active')} className={`text-[10px] px-2 py-1 rounded ${filterActive === 'active' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Active</button>
          <button onClick={() => setFilterActive('legacy')} className={`text-[10px] px-2 py-1 rounded ${filterActive === 'legacy' ? 'bg-slate-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>Legacy</button>
        </div>
      </div>

      {/* Forms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((form, i) => (
          <Card key={i} className={`transition-all hover:shadow-md ${form.is_active === false ? 'opacity-60' : ''}`} data-testid={`form-card-${form.form_code}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-slate-900">{form.form_code}</p>
                    {form.is_active !== false ? (
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-slate-300" />
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">{form.form_name}</p>
                </div>
                <span className={`text-[9px] px-2 py-0.5 rounded border font-semibold ${CATEGORY_COLORS[form.category] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                  {CATEGORY_LABELS[form.category] || form.category}
                </span>
              </div>

              {form.description && <p className="text-[11px] text-slate-500 leading-relaxed">{form.description}</p>}

              <div className="space-y-1.5">
                {form.section && (
                  <div className="flex gap-2 text-[10px]">
                    <span className="text-slate-400 min-w-[60px]">Section</span>
                    <span className="text-slate-700 font-medium">{form.section}</span>
                  </div>
                )}
                {form.due_date_shorthand && (
                  <div className="flex gap-2 text-[10px]">
                    <span className="text-slate-400 min-w-[60px] flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> Due</span>
                    <span className="text-slate-700">{form.due_date_shorthand}</span>
                  </div>
                )}
                {form.applicable_company_types && (
                  <div className="flex gap-2 text-[10px]">
                    <span className="text-slate-400 min-w-[60px]">Applies to</span>
                    <span className="text-slate-700">{form.applicable_company_types?.join(', ')}</span>
                  </div>
                )}
                {form.condition && (
                  <div className="flex gap-2 text-[10px]">
                    <span className="text-slate-400 min-w-[60px]">Condition</span>
                    <span className="text-amber-700 font-medium">{form.condition}</span>
                  </div>
                )}
                {form.replaced_by && (
                  <div className="flex gap-2 text-[10px]">
                    <span className="text-slate-400 min-w-[60px]">Replaced by</span>
                    <span className="text-blue-600 font-medium">{form.replaced_by}</span>
                  </div>
                )}
              </div>

              {/* CCFS Badge */}
              <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                {form.ccfs_covered ? (
                  <span className="text-[9px] font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1">
                    <Shield className="w-2.5 h-2.5" /> CCFS Covered
                    {form.ccfs_fee_multiplier && ` (${(form.ccfs_fee_multiplier * 100).toFixed(0)}% fee)`}
                  </span>
                ) : (
                  <span className="text-[9px] font-semibold px-2 py-0.5 rounded bg-slate-50 text-slate-400 border border-slate-200">Not under CCFS</span>
                )}
                {form.ccfs_immunity && (
                  <span className="text-[9px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Immunity</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-sm text-slate-400">No forms match your filters.</div>
      )}
    </div>
  );
}
